import SearchButton from './SearchButton';
import Link from 'next/link';
import HelpArticleLink from './HelpArticleLink';
import MDX from './MDX';
import { HELP_CATEGORIES } from 'content/helpContent';
import HelpFeedback from './HelpFeedback';
import { ArticleJsonLd, BreadcrumbJsonLd } from 'next-seo';

const subcategoryToTitle = {
  'easy-editor-blocks': 'Easy Editor Blocks',
  'site-builder-widgets': 'Site Builder Widgets',
};

export default function HelpArticle({ post, images, relatedArticles }) {
  const category = HELP_CATEGORIES.find(
    category => post.categories[0] === category.slug,
  );

  const breadcrumbs = [
    {
      position: 1,
      name: 'Help Center',
      item: `https://travelfeed.com/help`,
    },
    {
      position: 2,
      name: category.title,
      item: `https://travelfeed.com/help/category/${category.slug}`,
    },
  ];
  if (post.subcategory) {
    breadcrumbs.push(
      {
        position: 1,
        name: subcategoryToTitle[post.subcategory],
        item: `https://travelfeed.com/help/category/${category.slug}#${post.subcategory}`,
      },
      {
        position: 4,
        name: post.title,
        item: `https://travelfeed.com/help/article/${post.slug}`,
      },
    );
  } else {
    breadcrumbs.push({
      position: 3,
      name: post.title,
      item: `https://travelfeed.com/help/article/${post.slug}`,
    });
  }
  return (
    <>
      <BreadcrumbJsonLd itemListElements={breadcrumbs} />
      <ArticleJsonLd
        type="HelpPosting"
        url={`https://travelfeed.com/help/article/${post.slug}`}
        title={post.title}
        datePublished={post.createdAt}
        dateModified={post.updatedAt}
        authorName={{
          name: post.author,
        }}
        description={post.summary}
      />
      <div className="tw-flex tw-max-w-screen-lg tw-flex-col tw-py-10 tw-mx-auto tw-w-full tw-px-2.5 lg:tw-px-20">
        <SearchButton />
      </div>

      <div className="tw-border tw-border-solid tw-border-gray-200 dark:tw-border-gray-800 tw-bg-white/50 dark:tw-bg-black/50 tw-shadow-[inset_10px_-50px_94px_0_rgb(199,199,199,0.2)] dark:tw-shadow-none tw-backdrop-blur-lg">
        <div className="tw-grid tw-max-w-screen-lg tw-grid-cols-4 tw-gap-10 tw-py-10 tw-mx-auto tw-w-full tw-px-2.5 lg:tw-px-20">
          <div className="tw-col-span-4 tw-flex tw-flex-col tw-space-y-8 sm:tw-pr-10">
            <div className="tw-flex tw-items-center tw-space-x-2">
              <Link
                href="/help"
                className="tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-gray-800 dark:hover:tw-text-gray-400"
              >
                Help Center
              </Link>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="tw-h-4 tw-w-4 tw-text-gray-400"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 4.5l7.5 7.5-7.5 7.5"
                />
              </svg>
              <Link
                href={`/help/category/${category.slug}`}
                className="tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-gray-800 dark:hover:tw-text-gray-400"
              >
                {category.title}
              </Link>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="tw-h-4 tw-w-4 tw-text-gray-400"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 4.5l7.5 7.5-7.5 7.5"
                />
              </svg>
              {post.subcategory && (
                <>
                  <Link
                    href={`/help/category/${category.slug}#${post.subcategory}`}
                    className="tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-gray-800 dark:hover:tw-text-gray-400"
                  >
                    {subcategoryToTitle[post.subcategory]}
                  </Link>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="tw-h-4 tw-w-4 tw-text-gray-400"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.25 4.5l7.5 7.5-7.5 7.5"
                    />
                  </svg>
                </>
              )}
              <Link
                href={`/help/article/${post.slug}`}
                className="tw-truncate tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-gray-800 dark:hover:tw-text-gray-400"
              >
                {post.title}
              </Link>
            </div>
            <div className="tw-flex tw-flex-col tw-space-y-4">
              <Link href={`/help/article/${post.slug}`}>
                <h1 className="tw-font-display tw-text-3xl tw-font-bold tw-!leading-snug sm:tw-text-4xl">
                  {post.title}
                </h1>
              </Link>
              <p className="tw-text-gray-500">{post.summary}</p>
              {/* <Author username={post.author} updatedAt={post.updatedAt} /> */}
            </div>
            <MDX code={post.body.code} images={images} />
            {relatedArticles.length > 0 && (
              <div className="tw-flex tw-flex-col tw-space-y-4 tw-border-t tw-border-solid tw-border-0 tw-border-gray-200 dark:tw-border-gray-800 tw-pt-8">
                <h2 className="tw-font-display tw-text-xl tw-font-bold sm:tw-text-2xl">
                  Related Articles
                </h2>
                <div className="tw-grid tw-gap-2 tw-rounded-xl tw-border tw-border-solid tw-border-gray-200 tw-bg-white dark:tw-bg-gray-900 tw-p-4">
                  {relatedArticles.map(article => (
                    <HelpArticleLink key={article.slug} article={article} />
                  ))}
                </div>
              </div>
            )}
            <HelpFeedback slug={post.slug} />
          </div>
          {/* <div className="tw-sticky tw-top-20 tw-col-span-1 tw-hidden tw-flex-col tw-space-y-10 tw-divide-y tw-divide-gray-200 tw-self-start sm:tw-flex">
            {post.tableOfContents.length > 0 && (
              <TableOfContents items={post.tableOfContents} />
            )}
          </div> */}
        </div>
      </div>
    </>
  );
}
